import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { queryKeys } from '@/lib/queryKeys';
import { fetchActiveNotice, fetchNotices } from '@/services/notices';

// An outage notice is only useful if it reaches people already sitting on a
// tab, so re-check on focus and every few minutes rather than caching for long.
const ACTIVE_NOTICE_STALE_MS = 2 * 60 * 1000;

/** The notice shown in the top banner (null when staff have none active). */
export function useActiveNotice() {
    // `backoffice` is only readable as `authenticated`, so a signed-out tab
    // would poll nothing but permission errors.
    const { session } = useAuth();
    return useQuery({
        queryKey: queryKeys.notices.active,
        queryFn: fetchActiveNotice,
        enabled: !!session,
        staleTime: ACTIVE_NOTICE_STALE_MS,
        refetchInterval: ACTIVE_NOTICE_STALE_MS,
    });
}

/** Every notice, for the staff management page. */
export function useNotices() {
    return useQuery({
        queryKey: queryKeys.notices.list,
        queryFn: fetchNotices,
    });
}

/** Refresh both the staff list and the banner after a write. */
export function useInvalidateNotices() {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: queryKeys.notices.all });
}
